import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Alert, Button, Card, Input, InputNumber, Select, Space, Tag, Typography, message } from 'antd';
import { hasResource } from 'utils/resourceAuth';
import { ResourceCode } from 'constants/resourceCodes';
import {
    SORTER_APIS,
    SORTER_ENV_PRESETS,
    SorterApiGroup,
    SorterApiSpec,
    SorterField,
} from './sorterApiSpec';
import { buildSignatureHeaders, currentUnixSeconds } from './sorterApiSign';
import {
    GET_SORTER_API_CREDENTIALS,
    GetSorterApiCredentialsData,
    SorterApiCredential,
    GET_SORTER_MACHINE_OPTIONS,
    GetSorterMachineOptionsData,
    SorterMachineOption,
} from './graphql/sorterApiToolGraphql';

const { Text, Paragraph } = Typography;

const API_GROUP_LABELS: Record<SorterApiGroup, string> = {
    crossBelt: '交叉带 / DWS',
    rapidScan: '狂扫',
    smallParcelScanner: '小件快手',
};

const API_GROUP_OPTIONS = (['crossBelt', 'rapidScan', 'smallParcelScanner'] as SorterApiGroup[]).map((g) => ({
    label: API_GROUP_LABELS[g],
    options: SORTER_APIS.filter((a) => a.group === g).map((a) => ({ value: a.key, label: a.label })),
}));

const STORAGE_KEY = 'ops_sorter_api_tool';
const CURRENT_ENV = '__current__';
const CUSTOM_ENV = '__custom__';

/** 当前页面 origin（默认 baseURL：同源、免 CORS） */
const currentOrigin = (): string => (typeof window !== 'undefined' ? window.location.origin : '');

/** 仅存 baseURL/环境选择；签名凭证从后端查、不落盘 */
interface StoredConfig {
    envKey: string;
    baseUrl: string;
}

interface ResponseState {
    ok: boolean;
    status?: number;
    statusText?: string;
    timeMs: number;
    body: string;
    error?: string;
}

/** 数组字段值统一存 string[]，其它存 string；timestamp 存 string（秒） */
type FormValues = Record<string, string | string[]>;

const loadConfig = (): StoredConfig => {
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (raw) return JSON.parse(raw) as StoredConfig;
    } catch {
        // ignore
    }
    // 默认「当前地址」：baseURL = 当前页面 origin（同源直连、免 CORS）
    return { envKey: CURRENT_ENV, baseUrl: currentOrigin() };
};

const buildInitialValues = (spec: SorterApiSpec): FormValues => {
    const values: FormValues = {};
    spec.fields.forEach((f) => {
        if (f.type === 'array') {
            values[f.name] = f.example ? [f.example] : [];
        } else if (f.type === 'timestamp') {
            values[f.name] = String(currentUnixSeconds());
        } else {
            values[f.name] = f.example ?? '';
        }
    });
    return values;
};

/** 按 spec 把表单值拼成请求 payload（数组/数字/时间戳做类型转换，空的可选字段跳过） */
const buildPayload = (spec: SorterApiSpec, values: FormValues): Record<string, unknown> => {
    const payload: Record<string, unknown> = {};
    spec.fields.forEach((f) => {
        const v = values[f.name];
        if (f.type === 'array') {
            const arr = (v as string[]) || [];
            if (arr.length > 0 || f.required) payload[f.name] = arr;
        } else if (f.type === 'number') {
            const s = (v as string) ?? '';
            if (s !== '') payload[f.name] = Number(s);
            else if (f.required) payload[f.name] = null;
        } else if (f.type === 'timestamp') {
            // 时间戳不在页面展示，发送时实时生成
            payload[f.name] = currentUnixSeconds();
        } else {
            const s = (v as string) ?? '';
            if (s !== '' || f.required) payload[f.name] = s;
        }
    });
    return payload;
};

const SorterApiToolPage: React.FC = () => {
    const canOperate = hasResource([ResourceCode.menu.OPS_TOOL_WMS]);

    const [config, setConfig] = useState<StoredConfig>(loadConfig);
    const [appKey, setAppKey] = useState<string>('');
    const [apiKey, setApiKey] = useState<string>(SORTER_APIS[0].key);
    const spec = useMemo(() => SORTER_APIS.find((a) => a.key === apiKey) as SorterApiSpec, [apiKey]);
    const [values, setValues] = useState<FormValues>(() => buildInitialValues(SORTER_APIS[0]));
    const [sending, setSending] = useState(false);
    const [response, setResponse] = useState<ResponseState | null>(null);
    const [preview, setPreview] = useState<string>('');

    // 凭证从后端查库（channel api_version=4），用户下拉选，appSecret 不落盘
    const { data, loading: credLoading, error: credError } = useQuery<GetSorterApiCredentialsData>(
        GET_SORTER_API_CREDENTIALS,
        { fetchPolicy: 'network-only' },
    );
    const credentials: SorterApiCredential[] = data?.getSorterApiCredentials ?? [];
    const selectedCredential = credentials.find((c) => c.appKey === appKey);

    // 分拣机列表（machineCode 下拉用），跨仓全部，OPS_TOOL_WMS 门控
    const { data: machineData, loading: machineLoading } = useQuery<GetSorterMachineOptionsData>(
        GET_SORTER_MACHINE_OPTIONS,
        { fetchPolicy: 'network-only' },
    );
    const machineOptions: SorterMachineOption[] = machineData?.getSorterMachineOptions ?? [];

    useEffect(() => {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(config));
    }, [config]);

    // 切换接口时重置表单为该接口的示例值
    useEffect(() => {
        setValues(buildInitialValues(spec));
        setResponse(null);
        setPreview('');
    }, [spec]);

    const updateConfig = (patch: Partial<StoredConfig>) => setConfig((c) => ({ ...c, ...patch }));
    const updateValue = (name: string, v: string | string[]) => setValues((prev) => ({ ...prev, [name]: v }));

    const onEnvChange = (envKey: string) => {
        if (envKey === CURRENT_ENV) {
            updateConfig({ envKey, baseUrl: currentOrigin() });
            return;
        }
        if (envKey === CUSTOM_ENV) {
            updateConfig({ envKey });
            return;
        }
        const preset = SORTER_ENV_PRESETS.find((e) => e.key === envKey);
        updateConfig({ envKey, baseUrl: preset?.baseUrl ?? config.baseUrl });
    };

    const renderField = (f: SorterField) => {
        const v = values[f.name];
        if (f.type === 'sorter') {
            return (
                <Select
                    style={{ width: '100%' }}
                    value={(v as string) || undefined}
                    loading={machineLoading}
                    placeholder="选择分拣机（machineCode）"
                    showSearch
                    optionFilterProp="label"
                    onChange={(val: string) => updateValue(f.name, val)}
                    options={machineOptions.map((m) => ({
                        value: m.sorterCode,
                        label: `${m.sorterCode}${m.sorterName ? `（${m.sorterName}）` : ''}`,
                    }))}
                />
            );
        }
        if (f.type === 'array') {
            return (
                <Select
                    mode="tags"
                    style={{ width: '100%' }}
                    value={(v as string[]) || []}
                    placeholder="输入后回车添加多个值"
                    open={false}
                    onChange={(vals: string[]) => updateValue(f.name, vals)}
                />
            );
        }
        if (f.type === 'enum') {
            return (
                <Select
                    style={{ width: '100%' }}
                    value={(v as string) || undefined}
                    options={(f.enumValues || []).map((e) => ({ value: e, label: e }))}
                    onChange={(val: string) => updateValue(f.name, val)}
                />
            );
        }
        if (f.type === 'number') {
            return (
                <InputNumber
                    style={{ width: '100%' }}
                    value={(v as string) === '' ? undefined : Number(v)}
                    onChange={(val) => updateValue(f.name, val === null || val === undefined ? '' : String(val))}
                />
            );
        }
        return <Input value={v as string} onChange={(e) => updateValue(f.name, e.target.value)} />;
    };

    const onSend = async () => {
        if (!config.baseUrl) {
            message.warning('请先填写 baseURL');
            return;
        }
        if (!selectedCredential) {
            message.warning('请先选择签名凭证（App Key）');
            return;
        }
        setSending(true);
        setResponse(null);
        const payload = buildPayload(spec, values);
        const isGet = spec.method === 'GET';
        // GET：参数走 query string（浏览器 fetch 的 GET 不能带 body），body 空；POST：body 为 JSON
        const queryString = isGet
            ? Object.entries(payload)
                  .map(([k, val]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(val))}`)
                  .join('&')
            : '';
        const rawBody = isGet ? '' : JSON.stringify(payload);
        const url = `${config.baseUrl.replace(/\/$/, '')}${spec.path}${queryString ? `?${queryString}` : ''}`;

        try {
            const sign = await buildSignatureHeaders({
                appKey: selectedCredential.appKey,
                appSecret: selectedCredential.appSecret,
                method: spec.method,
                path: spec.path,
                queryString,
                rawBody,
            });
            const headers: Record<string, string> = { ...sign.headers };
            if (!isGet) headers['Content-Type'] = 'application/json';

            // 请求预览（不含 appSecret，仅展示派生签名）
            setPreview(
                [
                    `${spec.method} ${url}`,
                    ...Object.entries(headers).map(([k, val]) => `${k}: ${val}`),
                    '',
                    isGet ? '(GET 无 body)' : rawBody,
                ].join('\n'),
            );

            const started = performance.now();
            const resp = await fetch(url, {
                method: spec.method,
                headers,
                body: isGet ? undefined : rawBody,
            });
            const text = await resp.text();
            const timeMs = Math.round(performance.now() - started);
            let pretty = text;
            try {
                pretty = JSON.stringify(JSON.parse(text), null, 2);
            } catch {
                // 非 JSON 原样展示
            }
            setResponse({ ok: resp.ok, status: resp.status, statusText: resp.statusText, timeMs, body: pretty });
        } catch (e) {
            // 网络错误 / CORS 被拦截时 fetch 会 reject，且拿不到具体状态
            setResponse({
                ok: false,
                timeMs: 0,
                body: '',
                error: `请求失败：${(e as Error).message}。若为跨域(CORS)，需目标服务允许当前来源，前端无法绕过。`,
            });
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="p-4">
            <Typography.Title level={4}>分拣机接口调试工具</Typography.Title>
            <Paragraph type="secondary">
                前端直连分拣机 openapi（自动算签名 X-App-Key/X-Signature）。签名凭证从库(channel api_version=4)查、下拉选择，不在前端落盘。
            </Paragraph>

            <Card title="环境与签名凭证" size="small" className="mb-4">
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    <Space wrap>
                        <span>
                            环境：
                            <Select
                                style={{ width: 160, marginLeft: 8 }}
                                value={config.envKey}
                                onChange={onEnvChange}
                                options={[
                                    { value: CURRENT_ENV, label: '当前地址' },
                                    ...SORTER_ENV_PRESETS.map((e) => ({ value: e.key, label: e.label })),
                                    { value: CUSTOM_ENV, label: '自定义' },
                                ]}
                            />
                        </span>
                        <span>
                            baseURL：
                            <Input
                                style={{ width: 320, marginLeft: 8 }}
                                value={config.baseUrl}
                                onChange={(e) => updateConfig({ baseUrl: e.target.value })}
                                placeholder="https://swiftx.test10.tita.ltd"
                            />
                        </span>
                    </Space>
                    <span>
                        签名凭证：
                        <Select
                            style={{ width: 460, marginLeft: 8 }}
                            value={appKey || undefined}
                            loading={credLoading}
                            placeholder="选择渠道凭证（api_version=4）"
                            showSearch
                            optionFilterProp="label"
                            onChange={(v: string) => setAppKey(v)}
                            options={credentials.map((c) => ({
                                value: c.appKey,
                                label: `${c.channelName}（appKey: ${c.appKey}）`,
                            }))}
                        />
                    </span>
                    {credError ? (
                        <Alert type="error" showIcon message={`凭证查询失败：${credError.message}`} />
                    ) : null}
                    {!credLoading && !credError && credentials.length === 0 ? (
                        <Alert type="warning" showIcon message="未查到 api_version=4 且已配 appKey/appSecret 的渠道" />
                    ) : null}
                </Space>
            </Card>

            <Card title="接口与参数" size="small" className="mb-4">
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    <span>
                        接口：
                        <Select
                            style={{ width: 420, marginLeft: 8 }}
                            value={apiKey}
                            onChange={setApiKey}
                            options={API_GROUP_OPTIONS}
                        />
                        <Tag color="blue" style={{ marginLeft: 8 }}>{spec.method}</Tag>
                        <Text code>{spec.path}</Text>
                    </span>
                    {spec.fields.filter((f) => f.type !== 'timestamp').map((f) => (
                        <div key={f.name}>
                            <div style={{ marginBottom: 4 }}>
                                {f.required ? <Text type="danger">* </Text> : null}
                                <Text>{f.label}</Text>
                            </div>
                            {renderField(f)}
                        </div>
                    ))}
                    <Button type="primary" loading={sending} disabled={!canOperate} onClick={onSend}>
                        发送
                    </Button>
                </Space>
            </Card>

            {preview ? (
                <Card title="请求预览" size="small" className="mb-4">
                    <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: 0 }}>{preview}</pre>
                </Card>
            ) : null}

            {response ? (
                <Card
                    title={
                        response.error
                            ? '响应（错误）'
                            : `响应：HTTP ${response.status} ${response.statusText ?? ''} · ${response.timeMs}ms`
                    }
                    size="small"
                >
                    {response.error ? (
                        <Text type="danger">{response.error}</Text>
                    ) : (
                        <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: 0 }}>{response.body}</pre>
                    )}
                </Card>
            ) : null}
        </div>
    );
};

export default SorterApiToolPage;
